import { useCallback, useState } from 'react';
import { Logger } from '@saviynt/common';

import type {
  DataProviderPayload,
  PreviewDataProviderPayload,
} from '../../../shared/types';

import { FormForgeApiService } from '../../../shared/services';
import { withTimeout } from '../../../shared/utils';
import { API_TIMEOUT_IN_MS } from '../../FormBuilder';

export { parseDataProviderPreviewOptionTables } from '../utils/dataProviderUtils/validation';

interface ErrorResponse {
  message?: string;
  errorMessage?: string;
}

/**
 * Reads optional `customFormUid` from a request payload object without a typed
 * property read. That avoids `@typescript-eslint/no-unsafe-assignment` treating
 * the value as TypeScript's intrinsic `error` type on some payload unions.
 */
function readOptionalCustomFormUid(payload: object): string | undefined {
  if (!Object.prototype.hasOwnProperty.call(payload, 'customFormUid')) {
    return undefined;
  }

  const raw: unknown = Reflect.get(payload, 'customFormUid');

  if (typeof raw !== 'string' || raw === '') {
    return undefined;
  }

  return raw;
}

export class ApiError extends Error {
  status: number;

  details?: ErrorResponse;

  constructor(message: string, status: number, details?: ErrorResponse) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export interface ApiResponse {
  success?: boolean;
  errorMessage?: string;
  message?: string;
  data: {
    status: number;
    message: string;
    hasNext: boolean;
    data: {
      formItemId: string;
      data: { label: string; value: string; secondaryLabel?: string }[];
      page: number;
      size: number;
      totalItems: number;
      totalPages: number;
      hasNext: boolean;
    };
  };
  error?: ErrorResponse;
  status: number;
  ok?: boolean;
}

/**
 * Posts form data to the formforge service (preview mode).
 *
 * @param providerPayload The preview form payload to post.
 * @param signal Abort signal for cancellation.
 * @returns A promise resolving to a FormApiResponse.
 */
const postFormDataPreview = async (
  providerPayload: PreviewDataProviderPayload,
  signal?: AbortSignal
): Promise<ApiResponse> => {
  try {
    const response = (await FormForgeApiService.postDataProviderPreview(
      providerPayload,
      signal
    )) as ApiResponse;

    if (response.error || response.errorMessage) {
      Logger.log('response with error...', response);
    }

    Logger.log(response);

    return response;
  } catch (error) {
    Logger.log('error...', error);
    throw error;
  }
};

/**
 * Posts form data to the formforge service (live mode).
 *
 * @param providerPayload The live form payload to post.
 * @param signal Abort signal for cancellation.
 * @returns A promise resolving to a FormApiResponse.
 */
const postFormDataLive = async (
  providerPayload: DataProviderPayload,
  signal?: AbortSignal
): Promise<ApiResponse> => {
  try {
    const livePayload: DataProviderPayload = {
      urlSlug: providerPayload.urlSlug ?? '',
      formItemId: providerPayload.formItemId,
      page: providerPayload.page ?? 0,
      pageSize: providerPayload.pageSize ?? 10,
      dependencies: providerPayload.dependencies,
      searchText: providerPayload.searchText,
    };

    if (
      providerPayload.valuesToSearch !== undefined &&
      providerPayload.valuesToSearch !== null &&
      !(
        Array.isArray(providerPayload.valuesToSearch) &&
        providerPayload.valuesToSearch.length === 0
      )
    ) {
      livePayload.valuesToSearch = providerPayload.valuesToSearch;
    }

    const maybeUid = readOptionalCustomFormUid(providerPayload);

    if (maybeUid !== undefined) {
      livePayload.customFormUid = maybeUid;
    }

    const response = (await FormForgeApiService.postDataProvider(
      livePayload,
      signal
    )) as ApiResponse;

    if (response.error || response.errorMessage) {
      Logger.log('response with error...', response);
    }

    Logger.log(response);

    return response;
  } catch (error) {
    Logger.log('error...', error);
    throw error;
  }
};

interface MutationState {
  data?: ApiResponse;
  error?: ApiError;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

interface MutationCallbacks {
  onSuccess?: (data: ApiResponse) => void;
  onError?: (error: ApiError) => void;
}

interface MutationResultPreview extends MutationState {
  mutate: (
    payload: PreviewDataProviderPayload,
    callbacks?: MutationCallbacks,
    signal?: AbortSignal
  ) => Promise<void>;
  reset: () => void;
}

interface MutationResultLive extends MutationState {
  mutate: (
    payload: DataProviderPayload,
    callbacks?: MutationCallbacks,
    signal?: AbortSignal
  ) => Promise<void>;
  reset: () => void;
}

interface UseGetDataProviderOptions {
  isPreview?: boolean;
  /** Merged onto each request payload when the payload omits `customFormUid`. */
  customFormUid?: string;
}

/**
 * Combines external signal (user cancellation) with timeout signal.
 * Uses AbortSignal.any() if available, otherwise creates manual combination.
 */
const combineSignals = (
  externalSignal: AbortSignal,
  timeoutSignal: AbortSignal
): AbortSignal => {
  // Use AbortSignal.any() if available
  if ('any' in AbortSignal) {
    return (
      AbortSignal as typeof AbortSignal & {
        any: (signals: AbortSignal[]) => AbortSignal;
      }
    ).any([externalSignal, timeoutSignal]);
  }

  // Fallback: Create a new controller that aborts when either signal aborts
  const combinedController = new AbortController();
  const abortHandler = () => combinedController.abort();

  externalSignal.addEventListener('abort', abortHandler);
  timeoutSignal.addEventListener('abort', abortHandler);

  // Check if already aborted
  if (externalSignal.aborted || timeoutSignal.aborted) {
    combinedController.abort();
  }

  return combinedController.signal;
};

function useGetDataProvider(
  options?: UseGetDataProviderOptions
): MutationResultPreview | MutationResultLive {
  const { isPreview = true, customFormUid: customFormUidOption } =
    options ?? {};

  const mergePreviewCustomFormUid = useCallback(
    (payload: PreviewDataProviderPayload): PreviewDataProviderPayload => {
      const fromPayload = readOptionalCustomFormUid(payload);
      const fromOption =
        typeof customFormUidOption === 'string' && customFormUidOption !== ''
          ? customFormUidOption
          : undefined;
      const resolved = fromPayload ?? fromOption;

      if (resolved === undefined) {
        return payload;
      }

      return { ...payload, customFormUid: resolved };
    },
    [customFormUidOption]
  );

  const mergeLiveCustomFormUid = useCallback(
    (payload: DataProviderPayload): DataProviderPayload => {
      const fromPayload = readOptionalCustomFormUid(payload);
      const fromOption =
        typeof customFormUidOption === 'string' && customFormUidOption !== ''
          ? customFormUidOption
          : undefined;
      const resolved = fromPayload ?? fromOption;

      if (resolved === undefined) {
        return payload;
      }

      return { ...payload, customFormUid: resolved };
    },
    [customFormUidOption]
  );

  const [state, setState] = useState<MutationState>({
    isLoading: false,
    isError: false,
    isSuccess: false,
  });

  const mutatePreview = useCallback(
    async (
      payload: PreviewDataProviderPayload,
      callbacks?: MutationCallbacks,
      externalSignal?: AbortSignal
    ) => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        isError: false,
        isSuccess: false,
        error: undefined,
      }));

      try {
        // Always use withTimeout for timeout protection
        // If external signal provided, combine it with timeout signal
        const data = await withTimeout((timeoutSignal: AbortSignal) => {
          const signal = externalSignal
            ? combineSignals(externalSignal, timeoutSignal)
            : timeoutSignal;

          return postFormDataPreview(
            mergePreviewCustomFormUid(payload),
            signal
          );
        }, API_TIMEOUT_IN_MS);

        setState({ data, isLoading: false, isError: false, isSuccess: true });
        callbacks?.onSuccess?.(data);
      } catch (error) {
        // Ignore user-initiated abort errors only (not timeouts)
        // User aborts are DOMException with name 'AbortError'
        // Timeouts are ApiError with status 408 from withTimeout utility
        const isUserAbort =
          error instanceof Error &&
          error.name === 'AbortError' &&
          !(error instanceof ApiError);

        if (isUserAbort) {
          setState((prev) => ({ ...prev, isLoading: false }));

          return;
        }

        const apiError =
          error instanceof ApiError
            ? error
            : new ApiError('Unknown error', 500);

        setState({
          error: apiError,
          isLoading: false,
          isError: true,
          isSuccess: false,
        });
        callbacks?.onError?.(apiError);
      }
    },
    [mergePreviewCustomFormUid]
  );

  const mutateLive = useCallback(
    async (
      payload: DataProviderPayload,
      callbacks?: MutationCallbacks,
      externalSignal?: AbortSignal
    ) => {
      setState((prev) => ({
        ...prev,
        isLoading: true,
        isError: false,
        isSuccess: false,
        error: undefined,
      }));

      try {
        // Always use withTimeout for timeout protection
        // If external signal provided, combine it with timeout signal
        const data = await withTimeout((timeoutSignal: AbortSignal) => {
          const signal = externalSignal
            ? combineSignals(externalSignal, timeoutSignal)
            : timeoutSignal;

          return postFormDataLive(mergeLiveCustomFormUid(payload), signal);
        }, API_TIMEOUT_IN_MS);

        setState({ data, isLoading: false, isError: false, isSuccess: true });
        callbacks?.onSuccess?.(data);
      } catch (error) {
        // Ignore user-initiated abort errors only (not timeouts)
        // User aborts are DOMException with name 'AbortError'
        // Timeouts are ApiError with status 408 from withTimeout utility
        const isUserAbort =
          error instanceof Error &&
          error.name === 'AbortError' &&
          !(error instanceof ApiError);

        if (isUserAbort) {
          setState((prev) => ({ ...prev, isLoading: false }));

          return;
        }

        const apiError =
          error instanceof ApiError
            ? error
            : new ApiError('Unknown error', 500);

        setState({
          error: apiError,
          isLoading: false,
          isError: true,
          isSuccess: false,
        });
        callbacks?.onError?.(apiError);
      }
    },
    [mergeLiveCustomFormUid]
  );

  const reset = useCallback(() => {
    setState({ isLoading: false, isError: false, isSuccess: false });
  }, []);

  // When isPreview is explicitly true or false, return the specific type
  // When isPreview is undefined (defaults to true), return preview type
  if (isPreview === false) {
    return { ...state, mutate: mutateLive, reset } as MutationResultLive;
  }

  // For true or undefined, return preview type
  return { ...state, mutate: mutatePreview, reset } as MutationResultPreview;
}

export default useGetDataProvider;
